/**
 * PHFHowItWorks
 *
 * Numbered step-by-step process section displayed in a 4-column grid.
 * Each step shows a numbered circle, a connecting arrow (except the last),
 * a bold title, and a description. Background color is configurable.
 *
 * Props:
 * - title (string, required): Section heading displayed centered
 * - bgColor (string, required): Tailwind background color class (e.g. "bg-blue-navy")
 * - titleColor (string, optional): Tailwind text color class for the title. Defaults to "text-orange"
 * - steps (object[], required): Array of step objects, each with:
 *     - title (string): Bold step heading in white
 *     - description (string): Supporting text below the title
 */
const PHFHowItWorks = ({ title, steps, bgColor, titleColor = "text-orange" }) => {
  return (
    <div className={`${bgColor} py-20 px-4`}>
      <div className="max-w-6xl mx-auto">
        <h2 className={`!text-[36px] font-bold ${titleColor} font-poppins text-center mb-16`}>
          {title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="flex flex-col">
              {/* Step number + arrow */}
              <div className="flex items-center mb-6">
                <div className={`w-10 h-10 rounded-full border-2 border-blue ${titleColor === "text-orange" ? "bg-blue/30" : "bg-blue-light1/30"} flex items-center justify-center flex-shrink-0`}>
                  <span className={`${titleColor === "text-orange" ? "text-white" : "text-blue"} font-semibold text-[14px]`}>{index + 1}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 flex items-center ml-2">
                    <div className="flex-1 h-px bg-blue opacity-50" />
                    <svg className="w-3 h-3 text-blue opacity-50 -ml-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Step content */}
              <h3 className={`${titleColor === "text-orange" ? "text-white" : "text-blue-navy"} font-bold text-[16px] text-left mb-3 leading-snug`}>
                {step.title}
              </h3>
              <p className={`${titleColor === "text-orange" ? "text-blue-light1" : "text-blue-navy"} text-[14px] text-left leading-relaxed`}>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PHFHowItWorks;
